import { useState, useMemo, useCallback, useEffect } from 'react';

export interface UsePaginationProps<T> {
  data: T[];
  pageSize: number;
  getItemKey?: (item: T) => string | number; // Optional: for deduplication
}

export interface UsePaginationReturn<T> {
  currentPage: number;
  totalPages: number;
  paginatedData: T[];
  startRecord: number;
  endRecord: number;
  totalCount: number;
  handlePrevious: () => void;
  handleNext: () => void;
  setCurrentPage: (page: number) => void;
  hasDuplicates: boolean; // Flag to indicate if duplicates were detected
}

export function usePagination<T>({
  data,
  pageSize,
  getItemKey,
}: UsePaginationProps<T>): UsePaginationReturn<T> {
  const [currentPage, setCurrentPage] = useState(1);

  // Deduplicate data if getItemKey is provided
  const deduplicatedData = useMemo(() => {
    if (!getItemKey) return data;

    const seen = new Map<string | number, T>();
    const duplicates: (string | number)[] = [];

    data.forEach((item) => {
      const key = getItemKey(item);
      if (seen.has(key)) {
        duplicates.push(key);
      } else {
        seen.set(key, item);
      }
    });

    // Log warning if duplicates found
    if (duplicates.length > 0) {
      console.warn(
        `[usePagination] Found ${duplicates.length} duplicate item(s) in data. Keys:`,
        [...new Set(duplicates)].join(', '),
        '\nThis usually indicates duplicate data from the API. Duplicates have been removed.'
      );
    }

    return Array.from(seen.values());
  }, [data, getItemKey]);

  const hasDuplicates = getItemKey ? deduplicatedData.length < data.length : false;
  const totalCount = deduplicatedData.length;
  const totalPages = Math.ceil(totalCount / pageSize) || 1;

  // Reset to page 1 when data length OR pageSize changes
  useEffect(() => {
    setCurrentPage(1);
  }, [totalCount, pageSize]);

  // Calculate paginated data
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return deduplicatedData.slice(startIndex, endIndex);
  }, [deduplicatedData, currentPage, pageSize]);

  // Calculate record range for display
  const startRecord = totalCount > 0 ? (currentPage - 1) * pageSize + 1 : 0;
  const endRecord = Math.min(currentPage * pageSize, totalCount);

  // Navigation handlers
  const handlePrevious = useCallback(() => {
    setCurrentPage((prev) => Math.max(1, prev - 1));
  }, []);

  const handleNext = useCallback(() => {
    setCurrentPage((prev) => Math.min(totalPages, prev + 1));
  }, [totalPages]);

  return {
    currentPage,
    totalPages,
    paginatedData,
    startRecord,
    endRecord,
    totalCount,
    handlePrevious,
    handleNext,
    setCurrentPage,
    hasDuplicates,
  };
}
